import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/react-hooks'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
} from '@/components/ui-compat'
import { CloudUpload, Download, Eye, File, History, Pencil, RotateCcw, Save } from 'lucide-react'

import { FilePreviewModal, getPreviewFileType, isPreviewable } from '@/components/file-preview'
import type { PreviewFile } from '@/components/file-preview'

const isImageFile = (mimetype: string): boolean => getPreviewFileType(mimetype) === 'image'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { submittalStatusVariant } from '@/lib/status-variants'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import useRouter from '../../../hooks/useRouter'
import { formatBytes, formatDateTime } from '../../../utils/formatters'
import DocUploadModal from './DocUploadModal'

interface DocumentDetailModalProps {
  isOpen: boolean
  onClose: (changed?: boolean) => void
  documentId: string
  projectId?: string
}

interface Revision {
  id: string
  version: number
  uploadedAt: string
  comment: string | null
  isCurrentRevision: boolean
  downloadUrl: string
  previewUrl: string | null
  uploadedBy: {
    id: string
    name: string
  }
  file: {
    id: string
    filename: string
    mimetype: string
    size: number
  }
}

const DocumentDetailModal: React.FC<DocumentDetailModalProps> = ({
  isOpen,
  onClose,
  documentId,
  projectId,
}) => {
  const { t } = useTranslation()
  const { history } = useRouter()
  const { canCreate, canEdit } = useModuleActions('DOC_CONTROL', projectId)
  const [activeTab, setActiveTab] = useState('details')
  const [isEditing, setIsEditing] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  // Fetch document details
  const { data, loading, refetch } = useQuery(Query.DOC_DOCUMENT, {
    variables: { id: documentId },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.docDocument) {
        setName(data.docDocument.name)
        setDescription(data.docDocument.description || '')
      }
    },
  })

  // Update mutation
  const [updateDocument, { loading: updating }] = useMutation(Mutation.UPDATE_DOC_DOCUMENT, {
    onCompleted: () => {
      toastr.success('', t('DocControl_document_updated'))
      setIsEditing(false)
      setHasChanges(true)
      refetch()
    },
    onError: (error) => {
      toastr.error(t('Common_error'), error.message)
    },
  })

  // Restore revision mutation
  const [restoreRevision, { loading: restoring }] = useMutation(Mutation.RESTORE_DOC_REVISION, {
    onCompleted: () => {
      toastr.success('', t('DocControl_revision_restored'))
      setHasChanges(true)
      refetch()
    },
    onError: (error) => {
      toastr.error(t('Common_error'), error.message)
    },
  })

  // Submittal links (Task 17 step 3) — the reverse of Submittals' own "From Doc
  // Control" attachment picker: this shows which submittals reference THIS
  // document. Read-only here; linking/unlinking happens from the submittal side.
  const { data: docLinksData } = useQuery(Query.SUBMITTAL_DOC_LINKS, {
    variables: { documentId },
    skip: !documentId,
  })
  const submittalLinks: any[] = docLinksData?.submittalDocLinks || []

  const handleOpenSubmittal = (submittalIdToOpen: string) => {
    if (!projectId) return
    onClose(hasChanges)
    history.push(`/project/${projectId}/submittals/${submittalIdToOpen}`)
  }

  const document = data?.docDocument
  const revisions: Revision[] = document?.revisions || []

  // Build list of previewable files (images + PDFs + Excel) from revisions
  const previewableFiles: PreviewFile[] = revisions
    .filter((r) => (r.previewUrl || r.file?.id) && isPreviewable(r.file?.mimetype || ''))
    .map((r) => ({
      url: r.previewUrl || r.downloadUrl || '',
      title: `${document?.name || 'File'} (v${r.version})`,
      downloadUrl: r.downloadUrl,
      type: getPreviewFileType(r.file?.mimetype || '')!,
      fileId: r.file?.id,
      fileSize: r.file?.size,
    }))

  // Map revision ID to preview index
  const revisionIdToPreviewIndex = new Map<string, number>()
  let idx = 0
  revisions.forEach((r) => {
    if ((r.previewUrl || r.file?.id) && isPreviewable(r.file?.mimetype || '')) {
      revisionIdToPreviewIndex.set(r.id, idx)
      idx++
    }
  })

  const handleSave = async () => {
    if (!name.trim()) {
      toastr.warning('', t('DocControl_document_name_required'))
      return
    }

    await updateDocument({
      variables: {
        id: documentId,
        input: {
          name: name.trim(),
          description: description.trim() || null,
        },
      },
    })
  }

  const handleRestoreRevision = async (revisionId: string) => {
    if (!window.confirm(t('DocControl_restore_confirm'))) return
    await restoreRevision({ variables: { id: revisionId } })
  }

  const handleDownload = (downloadUrl: string) => {
    window.open(downloadUrl, '_blank')
  }

  const handlePreview = (revisionId: string, previewUrl: string | null, mimetype?: string) => {
    if (!previewUrl) return

    if (mimetype && isPreviewable(mimetype)) {
      const index = revisionIdToPreviewIndex.get(revisionId)
      if (index !== undefined) {
        setPreviewIndex(index)
      }
    } else {
      window.open(previewUrl, '_blank')
    }
  }

  const handleUploadComplete = async () => {
    setUploadModalOpen(false)
    setHasChanges(true)
    try {
      await refetch()
    } catch (error: any) {
      console.error('Failed to refetch document:', error)
      toastr.error(t('Common_error'), error.message)
    }
  }

  if (loading) {
    return (
      <Modal isOpen={isOpen} toggle={() => onClose(hasChanges)} size="lg" centered>
        <ModalBody className="flex justify-center py-5">
          <Spinner />
        </ModalBody>
      </Modal>
    )
  }

  if (!document) {
    return (
      <Modal isOpen={isOpen} toggle={() => onClose(hasChanges)} centered>
        <ModalBody>
          <Alert color="danger">{t('DocControl_document_not_found')}</Alert>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => onClose(hasChanges)}>
            {t('Common_close')}
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <>
      <Modal isOpen={isOpen} toggle={() => onClose(hasChanges)} size="lg" centered>
        <ModalHeader toggle={() => onClose(hasChanges)}>
          <span className="inline-flex items-center gap-2">
            <File className="h-4 w-4 text-primary" />
            {document.name}
            <Badge>v{document.currentRevision?.version || 1}</Badge>
          </span>
        </ModalHeader>

        <ModalBody>
          {/* Tabs */}
          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink
                className={cn({ active: activeTab === 'details' })}
                onClick={() => setActiveTab('details')}
              >
                <Pencil className="h-4 w-4 mr-2" />
                {t('DocControl_details')}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={cn({ active: activeTab === 'revisions' })}
                onClick={() => setActiveTab('revisions')}
              >
                <History className="h-4 w-4 mr-2" />
                {t('DocControl_revisions')} ({revisions.length})
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            {/* Details Tab */}
            <TabPane tabId="details">
              {isEditing ? (
                <form>
                  <div className="mb-2">
                    <Label className="mb-1 text-muted-foreground">{t('DocControl_document_name')}</Label>
                    <Input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('DocControl_document_name')}
                    />
                  </div>
                  <div className="mb-3">
                    <Label className="mb-1 text-muted-foreground">{t('DocControl_description')}</Label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t('DocControl_description_placeholder')}
                      rows={2}
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditing(false)
                        setName(document.name)
                        setDescription(document.description || '')
                      }}
                    >
                      {t('Common_cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={updating}>
                      {updating ? <Spinner size="sm" /> : <Save className="h-4 w-4" />}
                      {t('Common_save')}
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  {/* Compact info display */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-grow">
                      <div className="mb-2">
                        <small className="text-muted-foreground block">{t('DocControl_document_name')}</small>
                        <span className="font-medium">{document.name}</span>
                      </div>
                      {document.description && (
                        <div className="mb-2">
                          <small className="text-muted-foreground block">{t('DocControl_description')}</small>
                          <span>{document.description}</span>
                        </div>
                      )}
                    </div>
                    {canEdit && (
                      <Button variant="outline" onClick={() => setIsEditing(true)}>
                        <Pencil className="h-4 w-4" />
                        {t('Common_edit')}
                      </Button>
                    )}
                  </div>

                  {/* File info in compact grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm mb-3">
                    <div>
                      <span className="text-muted-foreground block">{t('DocControl_filename')}</span>
                      <span className="truncate block" title={document.filename}>{document.filename}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">{t('DocControl_size')}</span>
                      <span>{formatBytes(document.size)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">{t('DocControl_created_by')}</span>
                      <span>{document.createdBy?.name || '-'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">{t('DocControl_created_at')}</span>
                      <span>{formatDateTime(document.createdAt)}</span>
                    </div>
                    {document.folder && (
                      <div className="col-span-2 md:col-span-4">
                        <span className="text-muted-foreground block">{t('DocControl_folder')}</span>
                        <span>
                          {document.folder.path && document.folder.path.length > 0
                            ? document.folder.path.map((f: any) => f.name).join(' / ')
                            : document.folder.name}
                        </span>
                      </div>
                    )}
                    {submittalLinks.length > 0 && (
                      <div className="col-span-2 md:col-span-4">
                        <span className="text-muted-foreground block">{t('DocControl_related_submittals')}</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {submittalLinks.map((link) => (
                            <Badge
                              key={link.id}
                              variant={submittalStatusVariant(link.submittal?.status)}
                              className="cursor-pointer whitespace-nowrap font-medium"
                              title={t(`Submittal_status_${link.submittal?.status}`)}
                              onClick={() => handleOpenSubmittal(link.submittal?.id)}
                            >
                              {link.submittal?.displayNumber}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Image Preview - more compact */}
                  {document.previewUrl && isImageFile(document.mimetype) && (
                    <div className="border rounded p-2 bg-muted text-center max-h-[250px] overflow-hidden">
                      <img
                        src={document.previewUrl}
                        alt={document.name}
                        className="max-w-full max-h-[230px] object-contain cursor-pointer"
                        onClick={() => handlePreview(
                          document.currentRevision?.id || '',
                          document.previewUrl,
                          document.mimetype
                        )}
                        title={t('DocControl_click_to_enlarge')}
                      />
                    </div>
                  )}
                </>
              )}
            </TabPane>

            {/* Revisions Tab */}
            <TabPane tabId="revisions">
              {/* Upload New Revision Button — uploadDocRevision is DOC_CONTROL.CREATE */}
              {canCreate && (
                <div className="mb-3">
                  <Button onClick={() => setUploadModalOpen(true)}>
                    <CloudUpload className="h-4 w-4" />
                    {t('DocControl_upload_new_revision')}
                  </Button>
                </div>
              )}

              {revisions.length === 0 ? (
                <Alert color="info" className="mb-0">{t('DocControl_no_revisions')}</Alert>
              ) : (
                <div className="revision-list">
                  {revisions.map((revision) => (
                    <div
                      key={revision.id}
                      className={cn(
                        'flex items-center py-2 border-b',
                        revision.isCurrentRevision && 'bg-muted'
                      )}
                    >
                      {/* Thumbnail */}
                      <div className="mr-2 w-8 shrink-0">
                        {revision.previewUrl && isImageFile(revision.file?.mimetype || '') ? (
                          <img
                            src={revision.previewUrl}
                            alt={`v${revision.version}`}
                            className="h-8 w-8 rounded object-cover cursor-pointer"
                            onClick={() => handlePreview(revision.id, revision.previewUrl, revision.file?.mimetype)}
                          />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded bg-muted">
                            <File className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-grow text-sm">
                        <div className="flex items-center">
                          <Badge variant={revision.isCurrentRevision ? 'default' : 'secondary'} className="mr-1">
                            v{revision.version}
                          </Badge>
                          {revision.isCurrentRevision && (
                            <Badge variant="success">{t('DocControl_current')}</Badge>
                          )}
                          <span className="text-muted-foreground ml-2">{formatBytes(revision.file?.size || 0)}</span>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {revision.uploadedBy?.name || '-'} · {formatDateTime(revision.uploadedAt)}
                          {revision.comment && <span> · {revision.comment}</span>}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleDownload(revision.downloadUrl)}
                          title={t('Common_download')}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {revision.previewUrl && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handlePreview(revision.id, revision.previewUrl, revision.file?.mimetype)}
                            title={t('DocControl_preview')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {!revision.isCurrentRevision && canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleRestoreRevision(revision.id)}
                            title={t('DocControl_restore')}
                            disabled={restoring}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabPane>
          </TabContent>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" onClick={() => onClose(hasChanges)}>
            {t('Common_close')}
          </Button>
          <Button onClick={() => handleDownload(document.currentRevision?.downloadUrl || '')}>
            <Download className="h-4 w-4" />
            {t('Common_download')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Unified Upload Modal for New Revision */}
      <DocUploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onComplete={handleUploadComplete}
        documentId={documentId}
      />

      {/* File Preview Modal */}
      {previewIndex !== null && previewableFiles.length > 0 && (
        <FilePreviewModal
          isOpen={true}
          onClose={() => setPreviewIndex(null)}
          files={previewableFiles}
          currentIndex={previewIndex}
          onNavigate={setPreviewIndex}
        />
      )}
    </>
  )
}

export default DocumentDetailModal
